import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setGenericError } from "@/lib/api";
import { fr, en, LANGUAGES } from "@/i18n/strings";

const LANG_KEY = "clipx_lang";
const VERSION = `${Object.keys(en).length}-ex2`;
const I18nCtx = createContext(null);
const supported = (c) => LANGUAGES.some((l) => l.code === c);

export const getConsent = () => {
  try { return JSON.parse(localStorage.getItem("clipx_consent")); } catch (e) { return null; }
};

async function detectLang() {
  if (getConsent()?.preferences) {
    try {
      const { data } = await api.get("/geo");
      if (supported(data.lang)) return data.lang;
    } catch (e) { /* fall back to browser */ }
  }
  const nav = (navigator.language || "fr").slice(0, 2);
  return supported(nav) ? nav : "en";
}

async function loadDict(lang) {
  if (lang === "fr") return fr;
  if (lang === "en") return en;
  const cacheKey = `clipx_i18n_${lang}_${VERSION}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) return JSON.parse(cached);
  const { data } = await api.post("/i18n/translate", { lang, strings: en });
  localStorage.setItem(cacheKey, JSON.stringify(data.strings));
  return data.strings;
}

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(localStorage.getItem(LANG_KEY) || null);
  const [auto, setAuto] = useState(!localStorage.getItem(LANG_KEY));
  const [dict, setDict] = useState(fr);
  useEffect(() => { setGenericError(dict["err.generic"]); }, [dict]);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!lang) detectLang().then(setLangState);
  }, [lang]);

  useEffect(() => {
    const onConsent = () => { if (!localStorage.getItem(LANG_KEY)) detectLang().then(setLangState); };
    window.addEventListener("clipx-consent", onConsent);
    return () => window.removeEventListener("clipx-consent", onConsent);
  }, []);

  useEffect(() => {
    if (!lang) return;
    document.documentElement.lang = lang;
    document.documentElement.dir = LANGUAGES.find((l) => l.code === lang)?.rtl ? "rtl" : "ltr";
    setTranslating(true);
    loadDict(lang).then(setDict).catch(() => setDict(en)).finally(() => setTranslating(false));
  }, [lang]);

  const setLang = (code) => {
    if (code === "auto") {
      localStorage.removeItem(LANG_KEY);
      setAuto(true);
      setLangState(null);
      return;
    }
    localStorage.setItem(LANG_KEY, code);
    setAuto(false);
    setLangState(code);
  };

  const t = useCallback((key, vars = {}) => {
    let s = dict[key] ?? en[key] ?? fr[key] ?? key;
    Object.entries(vars).forEach(([k, v]) => { s = s.replaceAll(`{${k}}`, v); });
    return s;
  }, [dict]);

  return <I18nCtx.Provider value={{ lang: lang || "fr", auto, setLang, t, translating }}>{children}</I18nCtx.Provider>;
};

export const useI18n = () => useContext(I18nCtx);
